import { Box, useTheme } from "@mui/material";
import { usePagePrefs } from "@pepsico/pref-permissions";

import { useOrthoLodDebug } from "context/OrthoLodDebug";
import { formatBytesCompact } from "context/OrthoLodDebug/orthoLodDebug";

interface OrthoZoomDebugMarkerProps {}

const modeLabel = (mode: string): string => {
  switch (mode) {
    case "overview":
      return "LOD: COG overview";
    case "detail-full":
      return "LOD: COG full tiles";
    default:
      return `LOD: ${mode}`;
  }
};

const OrthoZoomDebugMarker: React.FC<OrthoZoomDebugMarkerProps> = () => {
  const theme = useTheme();
  const [prefs] = usePagePrefs();
  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { orthoLodDebug } = useOrthoLodDebug();
  const {
    detailCacheBlobBytes,
    detailCachePeakBlobBytes,
    detailCacheSize,
    geotiffBlockCacheBudgetBytes,
    jsHeap,
    mapZoom,
    mode,
    overviewBlobBytes,
    visibleTileCount,
  } = orthoLodDebug;

  if (locationUuid === "") {
    return null;
  }

  const heapLimitLabel =
    jsHeap == null
      ? "heap n/a"
      : `heap limit ${formatBytesCompact(jsHeap.jsHeapSizeLimit)} · used ${formatBytesCompact(jsHeap.usedJSHeapSize)}`;
  const overviewLabel = `overview ${formatBytesCompact(overviewBlobBytes)}`;
  const tileCacheLabel = `tiles ${detailCacheSize} · ${formatBytesCompact(detailCacheBlobBytes)} · peak ${formatBytesCompact(detailCachePeakBlobBytes)}`;
  const blockCacheLabel = `Range cache budget ${formatBytesCompact(geotiffBlockCacheBudgetBytes)}`;

  return (
    <Box
      aria-label="Orthophoto zoom level and memory debug marker"
      role="status"
      sx={{
        bgcolor: "rgba(0, 0, 0, 0.72)",
        borderRadius: 1,
        color: "common.white",
        fontFamily: "monospace",
        fontSize: 12,
        left: theme.spacing(1),
        lineHeight: 1.4,
        maxWidth: "min(720px, calc(100% - 16px))",
        pointerEvents: "none",
        position: "absolute",
        px: 1,
        py: 0.5,
        top: theme.spacing(1),
        whiteSpace: "normal",
        zIndex: 2,
      }}
      tabIndex={-1}
    >
      {`${modeLabel(mode)} · z${mapZoom.toFixed(2)} · visible ${visibleTileCount} · ${overviewLabel} · ${tileCacheLabel} · ${blockCacheLabel} · ${heapLimitLabel}`}
    </Box>
  );
};

export default OrthoZoomDebugMarker;
